import { useEffect, useState } from "react";
import {
  forgetResourceDraft,
  rememberResourceDraft,
  restoreResourceDraft,
  type ResourceDraftContext,
} from "@/lib/resource-draft";

export function useResourceDraft<T extends Record<string, string>>(
  context: ResourceDraftContext,
  fallback: T,
) {
  const [values, setValues] = useState(fallback);
  const [status, setStatus] = useState<
    "" | "restored" | "saved" | "unavailable"
  >("");
  const contextSignature = JSON.stringify(context);
  const fallbackSignature = JSON.stringify(fallback);

  // Initial server/client markup matches. Only recover browser-local input once
  // the editor is mounted, and never reuse it after the represented revision changes.
  useEffect(() => {
    const saved = restoreResourceDraft(
      JSON.parse(contextSignature),
      JSON.parse(fallbackSignature) as T,
    );
    setValues(saved);
    setStatus(JSON.stringify(saved) === fallbackSignature ? "" : "restored");
  }, [contextSignature, fallbackSignature]);

  function change(field: keyof T, value: string) {
    const next = { ...values, [field]: value };
    setValues(next);
    setStatus(rememberResourceDraft(context, next) ? "saved" : "unavailable");
  }

  function discard() {
    forgetResourceDraft(context);
    setStatus("");
  }

  return { values, change, discard, status };
}
